import { useState } from 'react';
import { ConsolePage, Icon, Panel, QR } from '@/components';
import { useVerifyCertificate } from '@/hooks/queries';
import { verifyUrl } from '../certificate/Certificate';
import type { CertificateVerification } from '@/repositories';

export function AdminVerify() {
  const verify = useVerifyCertificate();
  const [code, setCode] = useState('');
  const [result, setResult] = useState<CertificateVerification | null>(null);

  const run = () => {
    const id = code.trim();
    if (id) verify.mutate(id, { onSuccess: setResult });
  };

  const cert = result?.certificate;
  const valid = result?.result === 'valid';
  const notFound = result?.result === 'not-found';

  return (
    <ConsolePage title="Verify certificate" th="ตรวจสอบใบรับรอง" crumbs="RECORDS" maxWidth={1040}>
      <div style={{ display: 'grid', gridTemplateColumns: '380px minmax(0,1fr)', gap: 18, alignItems: 'start' }} className="max-md:!grid-cols-1">
        <Panel title="Verify by ID or QR">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <div style={{ padding: 14, background: 'var(--card-2)', borderRadius: 14, border: '1px solid var(--line)' }}>
              <QR value={verifyUrl(code || 'unknown')} size={150} />
            </div>
            <span className="muted" style={{ fontSize: 12.5 }}>Scan a certificate QR, or enter its ID</span>
          </div>
          <div className="field">
            <input className="input u-mono" aria-label="Certificate ID" value={code} onChange={(e) => setCode(e.target.value)} placeholder="CERT-ID" style={{ textAlign: 'center', letterSpacing: '.05em' }} />
          </div>
          <button className="btn btn-primary btn-block" style={{ marginTop: 12 }} onClick={run} disabled={verify.isPending || !code.trim()}>
            {verify.isPending ? 'Verifying…' : 'Verify certificate'}
          </button>
          <div className="muted" style={{ fontSize: 12, marginTop: 12, textAlign: 'center' }}>e.g. WP-ACLS-2026-0001</div>
        </Panel>

        <Panel title="Verification result">
          {verify.isPending ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 60, color: 'var(--ink-3)' }}>
              <div className="eyebrow">Checking hospital records…</div>
            </div>
          ) : !result ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
              <Icon name="shield" size={32} style={{ color: 'var(--ink-4)' }} />
              <div style={{ fontSize: 14 }}>Enter a certificate ID to verify.</div>
            </div>
          ) : notFound ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 48, textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--clay-soft)', color: 'var(--clay)', display: 'grid', placeItems: 'center' }}>
                <Icon name="x" size={32} stroke={2.4} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Not found</div>
              <div className="muted" style={{ fontSize: 13 }}>No certificate matches that ID in hospital records.</div>
            </div>
          ) : (
            cert && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: 16, borderRadius: 12, background: valid ? 'var(--sage-tint)' : 'var(--clay-soft)' }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', flex: 'none', background: valid ? 'var(--sage)' : 'var(--clay)', color: 'var(--on-accent)', display: 'grid', placeItems: 'center' }}>
                    <Icon name={valid ? 'check' : 'x'} size={26} stroke={2.4} />
                  </div>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 600, color: valid ? 'var(--sage-deep)' : '#7e4536' }}>{valid ? 'Valid certificate' : 'Expired certificate'}</div>
                    <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>{valid ? 'Authentic and within its validity period.' : 'Authentic, but past its expiry date.'}</div>
                  </div>
                </div>
                <div className="card divide">
                  {([
                    ['Holder', cert.instructor || '—'],
                    ['Course', cert.course],
                    ['Certificate ID', cert.id, true],
                    ['Host', cert.host],
                    ['Issued', cert.issued],
                    ['Expires', cert.expires],
                  ] as [string, string, boolean?][]).map((r, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px' }}>
                      <span className="muted" style={{ fontSize: 13 }}>{r[0]}</span>
                      <span style={{ fontSize: 13.5, fontWeight: 500 }} className={r[2] ? 'u-mono' : ''}>{r[1]}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </Panel>
      </div>
    </ConsolePage>
  );
}
